/* Home Policy Renewal - 2 Screen Flow */

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { unwrapResult } from "@reduxjs/toolkit";
import { toast } from "sonner";
import HomeRenewalSkeleton from "./components/HomeRenewalSkeleton";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import {
  policyView,
  getPolicyViewResponse,
  getPolicyViewLoading,
} from "../../../redux/features/policyDetailsSlice";
import { policyPremium } from "../../../redux/features/motor/renewalPremiumSlice";
import { getClientSource } from "../../../utils/getClientSource";
import EnhanceCoveragePage from "./steps/EnhanceCoveragePage";
import ReviewConfirmPage from "./steps/ReviewConfirmPage";

const RenewalHomePolicy = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const translate = useLanguageTranslate();

  // Current step state (0 = Enhance Coverage, 1 = Review and Confirm)
  const [currentStep, setCurrentStep] = useState(0);

  // Form data state for the flow
  const [formData, setFormData] = useState({
    // Cover toggles
    allRisk: false,
    vipCover: false,
    terrorism: false,
    domesticHelper: false,

    // Domestic Helper expanded fields
    domesticHelperFullName: "",
    domesticHelperDateOfBirth: {
      day: "",
      month: "",
      year: "",
      fullDate: "",
    },
    domesticHelperSumInsured: "500",
    domesticHelperCpr: "",
    cprFiles: [],

    // Original covers (for comparison)
    originalCovers: {
      allRisk: false,
      vipCover: false,
      terrorism: false,
      domesticHelper: false,
    },

    // Cover prices from API
    coverPrices: [],
  });

  // Premium data state (shared between steps)
  const [premiumData, setPremiumData] = useState(null);
  const [premiumLoading, setPremiumLoading] = useState(false);
  const [premiumError, setPremiumError] = useState(null);

  // Get selected policy from location state (passed from GenericSelectPoliciesPage)
  const selectedPolicy = location.state?.selectedPolicy;

  // Get policy view data from Redux
  const policyInfo = useSelector(getPolicyViewResponse);
  const isPolicyViewLoading = useSelector(getPolicyViewLoading);

  // Initialize and call policyView API on mount, then always call premium API
  useEffect(() => {
    const fetchPolicyAndPremium = async () => {
      if (!selectedPolicy) return;

      const polReq = {
        PolicyNumber: selectedPolicy.PolicyNumber,
        Channel: getClientSource(),
      };

      // Fetch policy details only if not already loaded for this policy
      if (
        !policyInfo?.PolicyHeader ||
        policyInfo?.PolicyHeader?.PolicyNumber !== selectedPolicy.PolicyNumber
      ) {
        try {
          console.log("📋 Calling policyView API...");
          const policyViewAction = await dispatch(policyView(polReq));
          unwrapResult(policyViewAction);
          console.log("✅ policyView completed.");
        } catch (error) {
          console.error("❌ policyView error:", error);
          // Non-fatal: still attempt premium fetch below
        }
      }

      // Always fetch premium on mount regardless of whether policyView was needed
      setPremiumLoading(true);
      setPremiumError(null);

      const premiumPayload = {
        PolicyNumber: selectedPolicy.PolicyNumber,
        Channel: getClientSource(),
      };

      console.log("✅ Calling premium API:", premiumPayload);

      try {
        const premiumAction = await dispatch(policyPremium(premiumPayload));
        const premiumResponse = unwrapResult(premiumAction);
        setPremiumData(premiumResponse);
        console.log("✅ Premium calculated:", premiumResponse);
      } catch (error) {
        console.error("❌ Premium API error:", error);
        setPremiumError(error.message || "Premium calculation failed");
        toast.error(translate(
          error.ErrorMessageEn || "Failed to calculate premium. Please try again.",
          error.ErrorMessageAr || "فشل في حساب القسط. يرجى المحاولة مرة أخرى."
        ));
      } finally {
        setPremiumLoading(false);
      }
    };

    fetchPolicyAndPremium();
  }, []);

  // If no selected policy, redirect back to renewal hub
  useEffect(() => {
    if (!selectedPolicy && !policyInfo?.PolicyHeader) {
      navigate("/renew");
    }
  }, [selectedPolicy, policyInfo, navigate]);

  // Initialize original covers from policy view when data loads
  useEffect(() => {
    if (policyInfo?.PolicyHeader?.Covers) {
      const existingCovers = policyInfo.PolicyHeader.Covers;
      const hasAllRisk = existingCovers.some(
        (cover) => cover.CoverCode === "CPRO21"
      );
      const hasVIP = existingCovers.some(
        (cover) => cover.CoverCode === "CPRO13"
      );
      const hasTerrorism = existingCovers.some(
        (cover) => cover.CoverCode === "CPRO33"
      );
      const hasDomesticHelper = existingCovers.some(
        (cover) => cover.CoverCode === "CPRO35"
      );

      setFormData((prev) => ({
        ...prev,
        allRisk: hasAllRisk,
        vipCover: hasVIP,
        terrorism: hasTerrorism,
        domesticHelper: hasDomesticHelper,
        originalCovers: {
          allRisk: hasAllRisk,
          vipCover: hasVIP,
          terrorism: hasTerrorism,
          domesticHelper: hasDomesticHelper,
        },
      }));
    }
  }, [policyInfo?.PolicyHeader?.Covers]);

  // Get policy details from PolicyHeader
  const policyDetailsInfo = policyInfo?.PolicyHeader;

  // Handle navigation between steps
  const goToNextStep = () => {
    setCurrentStep((prev) => Math.min(prev + 1, 1));
  };

  const goToPreviousStep = () => {
    if (currentStep === 0) {
      // If on first step, go back to renewal hub
      navigate("/renew");
    } else {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleCloseFlow = () => {
    navigate("/renew");
  };

  // Show skeleton while policy view or premium is loading
  if (isPolicyViewLoading || premiumLoading) {
    return <HomeRenewalSkeleton />;
  }

  // Render step components based on current step
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <EnhanceCoveragePage
            policyDetailsInfo={policyDetailsInfo}
            onContinue={goToNextStep}
            onBack={goToPreviousStep}
            formData={formData}
            setFormData={setFormData}
            premiumData={premiumData}
            setPremiumData={setPremiumData}
            premiumErrorFromParent={premiumError}
            premiumLoadingFromParent={premiumLoading}
          />
        );
      case 1:
        return (
          <ReviewConfirmPage
            policyDetailsInfo={policyDetailsInfo}
            formData={formData}
            onBack={goToPreviousStep}
            premiumData={premiumData}
            premiumError={premiumError}
          />
        );
      default:
        return null;
    }
  };

  return renderStep();
};

export default RenewalHomePolicy;
